<template>
  <div
    :class="[
      'custom-modal-payment',
      'modal',
      'fade',
      show ? 'disable-scroll' : '',
    ]"
    :id="modalId"
    tabindex="-1"
    role="dialog"
    :aria-labelledby="ariaLabelledBy"
    aria-hidden="true"
  >
    <div
      :class="[
        'modal-dialog',
        'modal-dialog-centered',
        size === 'sm' ? 'modal-sm' : '',
        size === 'md' ? 'modal-md' : '',
        size === 'lg' ? 'modal-lg' : '',
        size === 'xl' ? 'modal-xl' : ''
      ]"
      role="document"
    >
      <div class="modal-content p-0" :style="contentStyle">
        <!-- Header Slot -->
        <div v-if="$slots.header" class="modal-header custom-header" :style="headerStyle">
          <slot name="header"></slot>
          <button
            v-if="showCloseButton"
            type="button"
            class="close custom-close"
            data-dismiss="modal"
            aria-label="Close"
            @click.prevent="handleClose"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Subheader Slot (vinotinto strip) -->
        <div v-if="$slots.subheader" class="modal-subheader">
          <slot name="subheader"></slot>
        </div>

        <!-- Body Slot (default) -->
        <div class="modal-body rounded-0" :class="bodyClass" :style="bodyStyle">
          <slot></slot>
        </div>

        <!-- Footer Slot -->
        <div v-if="$slots.footer" class="modal-footer" :class="footerClass" :style="footerStyle">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useSlots, watch, onMounted, onBeforeUnmount, ref, nextTick } from 'vue';
import { Modal } from 'bootstrap';

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  modalId: {
    type: String,
    default: 'baseModal'
  },
  size: {
    type: String,
    default: 'normal',
    validator: (value) => ['normal', 'sm', 'md', 'lg', 'xl'].includes(value)
  },
  showCloseButton: {
    type: Boolean,
    default: true
  },
  ariaLabelledBy: {
    type: String,
    default: 'exampleModalLabel'
  },
  bodyClass: {
    type: String,
    default: 'p-4'
  },
  footerClass: {
    type: String,
    default: 'border-0 px-4 pb-4 justify-content-end'
  },
  contentStyle: {
    type: Object,
    default: () => ({})
  },
  headerStyle: {
    type: Object,
    default: () => ({})
  },
  bodyStyle: {
    type: Object,
    default: () => ({})
  },
  footerStyle: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'opened', 'closed']);
const $slots = useSlots();
const modalInstance = ref(null);
const _bootstrapVisible = ref(false);

const initializeModal = () => {
  const modalEl = document.getElementById(props.modalId);
  if (modalEl) {
    modalInstance.value = Modal.getOrCreateInstance(modalEl);
    
    modalEl.addEventListener('hidden.bs.modal', handleBootstrapClose);
    modalEl.addEventListener('shown.bs.modal', handleBootstrapShow);
  }
};

const destroyModal = () => {
  const modalEl = document.getElementById(props.modalId);
  if (modalEl) {
    modalEl.removeEventListener('hidden.bs.modal', handleBootstrapClose);
    modalEl.removeEventListener('shown.bs.modal', handleBootstrapShow);
  }
  if (modalInstance.value) {
    modalInstance.value.dispose();
    modalInstance.value = null;
  }
};

const handleBootstrapClose = () => {
  _bootstrapVisible.value = false;
  if (props.show) {
    emit('close');
  }
  emit('closed');
};

const handleBootstrapShow = () => {
  _bootstrapVisible.value = true;
  emit('opened');
};

const handleClose = () => {
  if (modalInstance.value) {
    modalInstance.value.hide();
  }
};

watch(() => props.show, async (newValue) => {
  if (newValue && !_bootstrapVisible.value && modalInstance.value) {
    modalInstance.value.show();
  } else if (!newValue && _bootstrapVisible.value && modalInstance.value) {
    modalInstance.value.hide();
  }
}, { flush: 'post' });

onMounted(() => {
  initializeModal();
  if (props.show && modalInstance.value) {
    modalInstance.value.show();
  }
});

onBeforeUnmount(() => {
  destroyModal();
});
</script>

<style scoped>
.disable-scroll {
  overflow: visible;
}

.custom-modal-payment .modal-dialog {
  min-height: 700px;
}

.custom-modal-payment .modal-content {
  border-radius: 22px;
  box-shadow: 0 8px 32px rgba(53, 16, 32, 0.18);
  background: #6d263a;
  padding: 0;
  border: none;
}

.custom-modal-payment .custom-header {
  background: #6d263a;
  color: #fff;
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  padding: 1.5rem 1rem 1rem 1rem;
  position: relative;
}

.custom-modal-payment .modal-subheader {
  background: #351020;
  color: #fff;
  padding: 1.5rem;
  margin: 0;
  border-top-left-radius: 44px;
  border-top-right-radius: 44px;
}

.custom-modal-payment .modal-body {
  background: #fff;

  max-height: 500px;
  overflow-y: auto;
  overflow-x: hidden;
}

.custom-modal-payment .modal-title {
  font-size: 2rem;
  font-weight: bold;
  letter-spacing: 1px;
}

.custom-modal-payment .custom-close {
  position: absolute;
  right: 1.5rem;
  top: 1.5rem;
  color: #fff;
  font-size: 2rem;
  background: none;
  border: none;
  opacity: 1;
}

.custom-modal-payment .modal-body.modal-body-scroll {
  max-height: 70vh;
  overflow-y: auto;
}

.custom-modal-payment .modal-footer {
  background: #fff;

}
</style>
